import type { Workbook, Worksheet } from "exceljs";
import type { ThgAnnualSummary } from "../../../types/thgAnnualSummary";
import { formatBerlin, ownerState } from "./thgFormat";
import { exportedRows, isUnpaidInternal } from "./thgInternal";
import { hasPayoutData, payoutExportStatus } from "./thgPayout";
import { COCHARGE_REVENUE_TEXTS } from "../../StationPerformance/StationPerformance.consts";
import { COPY } from "../ThgAnnualSummary.consts";

/**
 * Finance's payout sheet as a formatted workbook. Finance opens the export in Google Sheets, which takes
 * column widths, colours, alignment and number formats from an .xlsx but none of it from a CSV.
 *
 * Same seven columns as the CSV, in the order finance already uses:
 *   # | Name | E-Mail | Stationsnummer | Erste Transaktion | Energie {year} CDR (kWh) | Geld MWh = {rate} (EUR)
 * The energy column is the energy EXCLUDING free charging, which the money column is derived from.
 * When the year carries CLR-283 payout data, `Status Gutschrift | Gutschriftsnummer` follow as columns 8 and 9.
 *
 * Figures are stored as real numbers exactly as returned, with a display format at their fixed precision,
 * so the reader's own locale decides the separators (3.831,394 in German) and sums work. Text is stored
 * as text: an owner named "=HYPERLINK(…)" is a string cell here, never a formula, so it needs no escaping.
 */

const COLOR = {
  headerFill: "FF1D5B4C", // the sidebar's dark green
  headerText: "FFFFFFFF",
  finalFill: "FFE0F2F1",
  finalText: "FF1D5B4C",
  provisionalFill: "FFFFE0B2",
  provisionalText: "FF8A4B00",
  unpayableFill: "FFFFF3E0",
  unpayableEdge: "FFF57C00",
  totalsFill: "FFF1F5F4",
  border: "FFD5DDDB",
  muted: "FF5F6B69",
};

const KWH_FORMAT = "#,##0.000";
const EUR_FORMAT = '#,##0.00 "€"';

const COLUMNS = [
  { key: "index", minWidth: 6 },
  { key: "name", minWidth: 24 },
  { key: "email", minWidth: 28 },
  { key: "station", minWidth: 18 },
  { key: "firstYear", minWidth: 17 },
  { key: "energy", minWidth: 22 },
  { key: "payout", minWidth: 20 },
] as const;

/** CLR-283, after the seven: the payout status and the credit note number. */
const PAYOUT_COLUMNS = [
  { key: "payoutStatus", minWidth: 20 },
  { key: "creditNoteNumber", minWidth: 19 },
] as const;

/** The two figure columns: right-aligned, with number formats. */
const isFigure = (col: number) => col === 6 || col === 7;

const solid = (argb: string) => ({ type: "pattern" as const, pattern: "solid" as const, fgColor: { argb } });

const thin = { style: "thin" as const, color: { argb: COLOR.border } };

/**
 * Widest value in a column plus padding, so nothing has to be resized by hand. Deliberately uncapped:
 * data cells do not wrap, and Google Sheets hides whatever does not fit next to a filled cell.
 */
const fitWidth = (values: Array<string | number | null | undefined>, minWidth: number): number =>
  Math.max(minWidth, ...values.map((v) => String(v ?? "").length + 2));

/**
 * One label/value line above the table. The label spans the narrow "#" and the Name column and the value
 * the next three, so neither is cut off: Google Sheets does not let text overflow into a filled cell.
 */
const addContextRow = (sheet: Worksheet, label: string, value: string | number) => {
  const row = sheet.addRow([label, "", value]);
  sheet.mergeCells(row.number, 1, row.number, 2);
  sheet.mergeCells(row.number, 3, row.number, 5);
  row.getCell(1).font = { color: { argb: COLOR.muted } };
  row.getCell(3).alignment = { horizontal: "left" };
  return row;
};

/** A line across the whole sheet, for the title and notes. */
const addBanner = (sheet: Worksheet, text: string) => {
  const row = sheet.addRow([text]);
  sheet.mergeCells(row.number, 1, row.number, COLUMNS.length);
  return row;
};

const addContext = (sheet: Worksheet, summary: ThgAnnualSummary) => {
  const provisional = summary.status === "PROVISIONAL";

  const title = addBanner(sheet, `THG payouts ${summary.year}`);
  title.getCell(1).font = { bold: true, size: 14, color: { argb: COLOR.headerFill } };
  title.height = 24;

  const status = addContextRow(sheet, "Status", provisional ? "PROVISIONAL" : "FINAL");
  status.getCell(3).font = { bold: true, color: { argb: provisional ? COLOR.provisionalText : COLOR.finalText } };
  status.getCell(3).fill = solid(provisional ? COLOR.provisionalFill : COLOR.finalFill);

  if (provisional) {
    const note = addBanner(sheet, COPY.provisionalCsvNote);
    note.getCell(1).font = { bold: true, color: { argb: COLOR.provisionalText } };
    note.getCell(1).fill = solid(COLOR.provisionalFill);
  }

  addContextRow(sheet, "Rate (EUR/MWh)", summary.rateEurPerMwh);
  addContextRow(sheet, "Computed at (Europe/Berlin)", formatBerlin(summary.computedAt));
  addContextRow(sheet, "Computation ID", summary.computationId ?? "");
  if (summary.stationsWithoutOwner > 0) {
    const row = addContextRow(sheet, "Stations without owner", summary.stationsWithoutOwner);
    row.getCell(3).font = { bold: true, color: { argb: COLOR.unpayableEdge } };
  }
  if (summary.excludedSessionCount > 0) {
    addContextRow(sheet, "Excluded sessions", summary.excludedSessionCount);
  }

  const { excluded } = exportedRows(summary);
  if (excluded > 0) addBanner(sheet, COCHARGE_REVENUE_TEXTS.excludedInternal(excluded));

  // Says what the orange rows mean, so the colour is not left to be guessed.
  if ((summary.stations ?? []).some((station) => !isUnpaidInternal(station) && ownerState(station) !== "OK")) {
    const legend = addBanner(sheet, COPY.xlsxUnpayableLegend);
    legend.getCell(1).font = { italic: true, color: { argb: COLOR.provisionalText } };
    legend.getCell(1).fill = solid(COLOR.unpayableFill);
  }
  sheet.addRow([]);
};

export const buildThgWorkbook = async (summary: ThgAnnualSummary): Promise<Workbook> => {
  const { default: ExcelJS } = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "cocharge Back Office";
  workbook.created = new Date(summary.computedAt);

  const sheet = workbook.addWorksheet(`THG ${summary.year}`, {
    properties: { defaultRowHeight: 18 },
  });

  addContext(sheet, summary);
  const payouts = hasPayoutData(summary);
  const columnCount = COLUMNS.length + (payouts ? PAYOUT_COLUMNS.length : 0);

  const header = sheet.addRow([
    "#",
    "Name",
    "E-Mail",
    "Stationsnummer",
    "Erste Transaktion",
    `Energie ${summary.year} CDR (kWh)`,
    `Geld MWh = ${summary.rateEurPerMwh} (EUR)`,
    ...(payouts ? ["Status Gutschrift", "Gutschriftsnummer"] : []),
  ]);
  const headerRowNumber = header.number;
  header.height = 22;
  header.eachCell((c, col) => {
    c.font = { bold: true, color: { argb: COLOR.headerText } };
    c.fill = solid(COLOR.headerFill);
    c.alignment = { vertical: "middle", horizontal: col >= 5 || col === 1 ? "center" : "left", wrapText: true };
    c.border = { top: thin, bottom: thin, left: thin, right: thin };
  });

  // CLR-296: internal stations are not paid, so they are not on finance's sheet; # runs 1..n without gaps,
  // and the total is the sum of the rows that are on it.
  const { stations, paidEnergyKwh, payoutEur } = exportedRows(summary);
  stations.forEach((station, index) => {
    const row = sheet.addRow([
      index + 1,
      station.owner?.name ?? "",
      station.owner?.email ?? "",
      station.stationId,
      station.firstTransactionYear ?? "",
      station.paidEnergyKwh,
      station.payoutEur,
      ...(payouts ? [payoutExportStatus(station.payout), station.payout?.creditNote?.number ?? ""] : []),
    ]);
    const unpayable = ownerState(station) !== "OK";
    row.eachCell({ includeEmpty: true }, (c, col) => {
      c.border = { bottom: thin, left: thin, right: thin };
      c.alignment = { vertical: "middle", horizontal: col === 1 || col === 5 ? "center" : isFigure(col) ? "right" : "left" };
      if (unpayable) c.fill = solid(COLOR.unpayableFill);
    });
    row.getCell(6).numFmt = KWH_FORMAT;
    row.getCell(7).numFmt = EUR_FORMAT;
    if (unpayable) {
      // A thick orange edge, as on screen: this station has nobody to pay.
      row.getCell(1).border = { ...row.getCell(1).border, left: { style: "thick", color: { argb: COLOR.unpayableEdge } } };
    }
  });

  const totals = sheet.addRow([
    "Total",
    "",
    "",
    "",
    "",
    paidEnergyKwh,
    payoutEur,
    ...(payouts ? ["", ""] : []),
  ]);
  totals.eachCell({ includeEmpty: true }, (c, col) => {
    c.font = { bold: true };
    c.fill = solid(COLOR.totalsFill);
    c.border = { top: { style: "medium", color: { argb: COLOR.headerFill } }, bottom: thin, left: thin, right: thin };
    c.alignment = { vertical: "middle", horizontal: isFigure(col) ? "right" : "left" };
  });
  totals.getCell(6).numFmt = KWH_FORMAT;
  totals.getCell(7).numFmt = EUR_FORMAT;

  const widths = [
    fitWidth([stations.length], COLUMNS[0].minWidth),
    fitWidth(stations.map((s) => s.owner?.name), COLUMNS[1].minWidth),
    fitWidth(stations.map((s) => s.owner?.email), COLUMNS[2].minWidth),
    fitWidth(stations.map((s) => s.stationId), COLUMNS[3].minWidth),
    COLUMNS[4].minWidth,
    COLUMNS[5].minWidth,
    COLUMNS[6].minWidth,
    ...(payouts
      ? [
          fitWidth(stations.map((s) => payoutExportStatus(s.payout)), PAYOUT_COLUMNS[0].minWidth),
          fitWidth(stations.map((s) => s.payout?.creditNote?.number), PAYOUT_COLUMNS[1].minWidth),
        ]
      : []),
  ];
  widths.forEach((width, i) => {
    sheet.getColumn(i + 1).width = width;
  });

  // Header stays in view while scrolling a few hundred stations, and every column can be filtered.
  sheet.views = [{ state: "frozen", ySplit: headerRowNumber }];
  // Also on a year with no stations: the range is then the header row alone, which is valid.
  sheet.autoFilter = {
    from: { row: headerRowNumber, column: 1 },
    to: { row: headerRowNumber + stations.length, column: columnCount },
  };

  return workbook;
};

export const thgXlsxFilename = (summary: Pick<ThgAnnualSummary, "year" | "status">): string =>
  `thg-payouts-${summary.year}-${summary.status}.xlsx`;

const XLSX_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** Builds the workbook and hands it to the browser. */
export const downloadThgXlsx = async (
  summary: ThgAnnualSummary,
  save: (blob: Blob, filename: string) => void,
): Promise<void> => {
  const workbook = await buildThgWorkbook(summary);
  const buffer = await workbook.xlsx.writeBuffer();
  save(new Blob([buffer], { type: XLSX_TYPE }), thgXlsxFilename(summary));
};
